import { useState, useEffect } from 'react'
import { GraduationCap, ClipboardCheck, Users, Loader2, UserCheck } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'

interface Module {
  id: string
  name: string
  description: string
  icon: typeof ClipboardCheck
  color: string
  route: string
  stats?: {
    label: string
    value: number
  }[]
}

export function HomePage() {
  const navigate = useNavigate()

  const [classCount, setClassCount] = useState(0)
  const [studentCount, setStudentCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      setLoading(true)
      const classes = await api.getClasses()

      setClassCount(classes.length)

      const total = classes.reduce((sum: number, c: any) => sum + (c.student_count || 0), 0)
      setStudentCount(total)
    } catch (err) {
      console.error('Failed to load stats:', err)
    } finally {
      setLoading(false)
    }
  }

  const modules: Module[] = [
    {
      id: 'classes',
      name: 'Gestion Classes',
      description: 'Créer classes, importer élèves CSV/Excel, organisation',
      icon: Users,
      color: 'from-emerald-500 to-teal-500',
      route: '/classes',
      stats: [
        { label: 'Classes', value: classCount }
      ]
    },
    {
      id: 'students',
      name: 'Gestion Élèves',
      description: 'Ajouter, modifier, rechercher vos élèves par classe',
      icon: UserCheck,
      color: 'from-blue-500 to-cyan-500',
      route: '/students',
      stats: [
        { label: 'Élèves', value: studentCount }
      ]
    },
    {
      id: 'evaluation',
      name: 'Évaluation Comportementale',
      description: 'Créer et gérer les évaluations comportementales de vos élèves',
      icon: ClipboardCheck,
      color: 'from-violet-500 to-purple-500',
      route: '/modules/evaluation'
    }
  ]

  const handleModuleClick = (module: Module) => {
    navigate(module.route)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <GraduationCap className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Évaluation Comportementale</h1>
            <p className="text-xs text-gray-500">Suivi du comportement des élèves</p>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Bienvenue 👋
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            Gérez vos classes, vos élèves et leurs évaluations comportementales
          </p>
        </div>

        {/* Module Cards Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <p className="ml-3 text-gray-600">Chargement...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {modules.map((module) => {
              const Icon = module.icon

              return (
                <div
                  key={module.id}
                  onClick={() => handleModuleClick(module)}
                  className="relative group rounded-2xl p-8 bg-white/80 backdrop-blur-sm border border-gray-200 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] cursor-pointer hover:border-transparent"
                >
                  {/* Icon with gradient */}
                  <div className={`
                    w-16 h-16 rounded-xl mb-6
                    bg-gradient-to-br ${module.color}
                    flex items-center justify-center
                    group-hover:scale-110
                    transition-transform duration-300 shadow-lg
                  `}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {module.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed min-h-[40px]">
                    {module.description}
                  </p>

                  {/* Stats */}
                  {module.stats && (
                    <div className="flex gap-6 pt-6 border-t border-gray-100">
                      {module.stats.map((stat, idx) => (
                        <div key={idx}>
                          <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                          <div className="text-xs text-gray-500">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Active indicator */}
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-1 text-xs font-medium text-blue-600">
                      Ouvrir
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
